/*
 * Funnel Analysis (CXO). The sankey chart code below is unchanged from the old page;
 * only the page around it uses the custom components.
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import ReactECharts from "echarts-for-react";
import { ArrowRight, BarChart3, CreditCard, ShoppingCart, Users } from "lucide-react";
import { getFunnelData, getMetricsSource } from "../../Action/actionFile";
import { CustomDatePicker, CustomEmptyState, CustomPageLayout, CustomStatCard } from "../../Component/customComponents";

const Panel = ({ title, extra, children }) => (
  <section className="bg-white border rounded-3 shadow-sm">
    <div className="d-flex justify-content-between align-items-center gap-2 px-3 py-2 border-bottom flex-wrap">
      <h6 className="mb-0 fw-bold">{title}</h6>
      {extra}
    </div>
    {children}
  </section>
);

/** "+12 vs Yesterday" and a % trend for a stat card (same numbers the old cards showed) */
const vsYesterday = (cur = 0, prev = 0) => {
  const diff = (cur || 0) - (prev || 0);
  return {
    subtitle: `${diff > 0 ? "+" : ""}${diff} vs Yesterday`,
    trend: prev ? { value: Math.round((diff / prev) * 1000) / 10, label: "" } : undefined,
  };
};

const FunnelAnalysis = () => {
  const dispatch = useDispatch();

  //data handle
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [sourcesData, setSourcesData] = useState([]);
  const [metricsData, setMetricsData] = useState({});
  const [funnelData, setFunnelData] = useState({});
  const [selectedSource, setSelectedSource] = useState({});
  const [loading, setLoading] = useState(false);

  function cleanSankeyLinks(links, entryNodes = ["Home", "Landing Page"]) {
    const graph = {};
    const linkMap = {};
    const cleaned = [];

    function hasPath(start, target, visited = new Set()) {
      if (start === target) return true;
      if (!graph[start]) return false;

      for (const next of graph[start]) {
        if (!visited.has(next)) {
          visited.add(next);
          if (hasPath(next, target, visited)) return true;
        }
      }
      return false;
    }

    for (const link of links) {
      if (!link.source || !link.target) continue;

      // skip empty flows — the API sends value 0 for paths nobody took;
      // drawing them (as 1) added fake ribbons and inflated node totals
      if (!(Number(link.value) > 0)) continue;

      // remove self links
      if (link.source === link.target) continue;

      // prevent incoming links to entry nodes (like Home)
      if (entryNodes.includes(link.target)) continue;

      // remove cycles
      if (hasPath(link.target, link.source)) continue;

      const key = `${link.source}->${link.target}`;

      // merge duplicates
      if (linkMap[key]) {
        linkMap[key].value += Number(link.value);
        continue;
      }

      // update graph
      if (!graph[link.source]) graph[link.source] = [];
      graph[link.source].push(link.target);

      const newLink = {
        source: link.source,
        target: link.target,
        value: Number(link.value)
      };

      linkMap[key] = newLink;
      cleaned.push(newLink);
    }

    return cleaned;
  }

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setFunnelData({}); // don't keep the previous day's funnel while loading
    dispatch(
      getMetricsSource(
        { date: date },
        (res) => {
          setSourcesData(res?.data?.sources || []);
          setMetricsData(res?.data?.metrics || {});
          setSelectedSource(res?.data?.sources?.[0] || {});
          setLoading(false);
        },
        () => setLoading(false),
      ),
    );
  }, [date]);

  useEffect(() => {
    if (selectedSource?.page) {
      dispatch(
        getFunnelData({ date: date, source: selectedSource?.page }, (res) => {
          const cleanedLinks = cleanSankeyLinks(res?.data?.links, [
            res?.data?.funnel?.[0]?.stage || "Home",
            "Landing Page"
          ]);

          setFunnelData({
            ...res?.data,
            links: cleanedLinks,
          });
        }),
      );
    }
  }, [selectedSource]);

  const visitorDiff = (metricsData?.current?.visitors || 0) - (metricsData?.previous?.visitors || 0);
  const sessionDiff = (metricsData?.current?.sessions || 0) - (metricsData?.previous?.sessions || 0);
  const addToCartDiff = (metricsData?.current?.addToCart || 0) - (metricsData?.previous?.addToCart || 0);
  const paymentsDiff = (metricsData?.current?.payments || 0) - (metricsData?.previous?.payments || 0);

  // ---------------------------------------------------------------------
  // Sankey / funnel chart data prep
  // ---------------------------------------------------------------------

  // The "root" of this funnel graph is whichever stage the backend lists
  // first in funnelData.funnel. Everything below keys off this value
  // instead of a literal "Home" string, so a different selected source
  // (or a backend change to stage naming) doesn't silently break things.
  const rootStageName = funnelData?.funnel?.[0]?.stage || "Home";

  // Per-stage user counts and drop-offs exactly as the API reports them
  // (funnelData.funnel / funnelData.dropOff). These are what the node
  // labels display. (The API sometimes repeats the last stage with a 0
  // value — keep the first occurrence only.)
  const stageUsers = {};
  (funnelData?.funnel || []).forEach((f) => {
    if (f?.stage && !(f.stage in stageUsers)) stageUsers[f.stage] = Number(f.value) || 0;
  });
  const stageDropOff = {};
  (funnelData?.dropOff || []).forEach((d) => {
    if (d?.from) stageDropOff[d.from] = Number(d.dropOff) || 0;
  });

  // Root total: the funnel's own count first; fall back to the selected
  // source's entry in Traffic Sources, then the unscoped metrics total.
  const selectedSourceEntry = sourcesData?.find((s) => s.page === selectedSource?.page);
  const totalVisitors =
    stageUsers[rootStageName] ??
    selectedSourceEntry?.users ??
    (metricsData?.current?.visitors || 0);
  const realLinks = funnelData?.links || [];

  // 1. Column order comes from funnelData.funnel itself — the backend's
  //    own primary stage sequence — instead of a hardcoded list of stage
  //    names.
  const depthMap = { [rootStageName]: 0 };
  (funnelData?.funnel || []).forEach((f) => {
    if (f?.stage && !(f.stage in depthMap)) depthMap[f.stage] = Object.keys(depthMap).length;
  });

  // 2. Any node the API returned that ISN'T one of those primary stages
  //    (a side/detour page) isn't in the map yet. Resolve its column from
  //    the graph itself: one past whichever of its sources is already
  //    resolved. Runs as a fixed-point loop since a node can depend on
  //    another still-unresolved node.
  let unresolved = (funnelData?.nodes || [])
    .map((n) => n.name)
    .filter((name) => !(name in depthMap));
  for (let pass = 0; pass < unresolved.length + 1 && unresolved.length; pass++) {
    unresolved = unresolved.filter((name) => {
      const sourceDepths = realLinks
        .filter((l) => l.target === name && l.source in depthMap)
        .map((l) => depthMap[l.source]);
      if (!sourceDepths.length) return true; // no resolved source yet, try next pass
      depthMap[name] = Math.max(...sourceDepths) + 1;
      return false;
    });
  }

  // 3. Flow totals into / out of each node.
  const incoming = {};
  const outgoing = {};
  realLinks.forEach((l) => {
    incoming[l.target] = (incoming[l.target] || 0) + (l.value || 0);
    outgoing[l.source] = (outgoing[l.source] || 0) + (l.value || 0);
  });

  // ---------------------------------------------------------------------
  // Layered layout — one column per step, every column reads the same way:
  //   top    → the step itself
  //   middle → flows passing through this column on their way to a later
  //            step (e.g. Home → Online Coaching), drawn as continuous
  //            ribbons through hidden "via" points so they never cut
  //            diagonally across other steps
  //   bottom → the previous step's Drop-off (grey), so drop-offs form one
  //            lane along the bottom of the chart
  // layoutIterations: 0 keeps this order (ECharts stacks each column in
  // data order).
  // ---------------------------------------------------------------------

  const baseNodes = funnelData?.nodes || [];

  // ECharts' rich-text formatter parses "{styleName|text}" tokens with a
  // regex that only accepts letters/digits/underscore in styleName. Strip
  // anything that isn't a word character wherever a node name becomes
  // part of a rich-text key.
  const sanitizeKey = (name) => name.replace(/[^a-zA-Z0-9_]/g, "");

  // Several columns in a fixed-width chart don't leave much room per
  // column — break longer names onto two lines.
  const wrapTitle = (name) => {
    if (name.length <= 16) return name;
    const words = name.split(" ");
    const mid = Math.ceil(words.length / 2);
    return `${words.slice(0, mid).join(" ")}\n${words.slice(mid).join(" ")}`;
  };

  // Biggest real flow in the graph. Used to (a) give tiny steps a visible
  // minimum height and (b) cap how wide a drop-off ribbon is DRAWN — the
  // root drop-off (~90% of visitors) would otherwise squeeze every other
  // step to a few pixels. Real counts are always shown in labels/tooltips.
  const maxFlow = Math.max(
    1,
    ...baseNodes.map((n) => Math.max(incoming[n.name] || 0, outgoing[n.name] || 0)),
  );
  const minNodeValue = maxFlow * 0.08;

  const STEP_COLORS = [
    "#3b82f6", "#f59e0b", "#84cc16", "#10b981",
    "#0ea5e9", "#6366f1", "#a855f7", "#ec4899", // no red/grey: those mean drop-off
  ];
  const DROP_COLOR = "#9ca3af";

  // 4. Step nodes (top of each column).
  const rich = {
    dropTitle: { fontWeight: 600, fontSize: 11, color: "#4b5563", lineHeight: 15 },
    dropCount: { fontSize: 10, color: "#dc2626", fontWeight: 600, lineHeight: 14 },
  };
  const nodesWithMeta = baseNodes.map((node, index) => {
    const users =
      node.name === rootStageName
        ? totalVisitors
        : stageUsers[node.name] ?? incoming[node.name] ?? 0;
    const percent = totalVisitors ? ((users / totalVisitors) * 100).toFixed(1) : "0.0";
    const key = sanitizeKey(node.name);
    const color = STEP_COLORS[index % STEP_COLORS.length];

    // Drop-off for EVERY step: the API's dropOff list when it has the
    // stage, otherwise users who didn't continue (users − outflow). A step
    // with no outgoing flow is the end of the funnel → "converted".
    const isFinal = !outgoing[node.name];
    const dropped = isFinal
      ? 0
      : stageDropOff[node.name] ?? Math.max(users - (outgoing[node.name] || 0), 0);
    const dropRate = users ? ((dropped / users) * 100).toFixed(1) : "0.0";

    rich[`title_${key}`] = { fontWeight: 700, fontSize: 12, lineHeight: 16, color: "#111827" };
    rich[`pct_${key}`] = { fontWeight: 700, fontSize: 10, color, lineHeight: 14 };
    rich[`count_${key}`] = { fontSize: 10, color: "#4b5563", lineHeight: 14 };
    rich[`done_${key}`] = { fontSize: 10, color: "#16a34a", fontWeight: 700, lineHeight: 14 };

    return {
      name: node.name,
      value: minNodeValue,
      users,
      percent,
      dropped,
      dropRate,
      isFinal,
      depth: depthMap[node.name],
      itemStyle: { color }
    };
  });
  const nodeMetaByName = Object.fromEntries(nodesWithMeta.map((n) => [n.name, n]));

  // 5. Flows, split into one segment per column so a flow that skips
  //    steps runs through each column it crosses (via hidden points)
  //    instead of slicing across other steps. Colored by origin step.
  const viaNodes = [];
  const flowLinks = [];
  realLinks.forEach((l, li) => {
    const color = nodeMetaByName[l.source]?.itemStyle.color || "#94a3b8";
    const from = depthMap[l.source];
    const to = depthMap[l.target];
    const flow = { from: l.source, to: l.target, value: l.value };
    let prev = l.source;
    if (from != null && to != null && to - from > 1) {
      for (let d = from + 1; d < to; d++) {
        const via = `__via_${li}_${d}`;
        viaNodes.push({
          name: via,
          isVia: true,
          depth: d,
          // Same colour/opacity as the ribbon so the flow reads as one
          // continuous band through this column (opacity 0 left white gaps).
          itemStyle: { color, opacity: 0.4 },
          label: { show: false },
          emphasis: { disabled: true }
        });
        flowLinks.push({ source: prev, target: via, value: l.value, flow, showPill: prev === l.source, lineStyle: { color } });
        prev = via;
      }
    }
    flowLinks.push({ source: prev, target: l.target, value: l.value, flow, showPill: prev === l.source, lineStyle: { color } });
  });

  // 6. Drop-off nodes (bottom of the NEXT column) + grey ribbons.
  const dropNodes = [];
  const dropLinks = [];
  nodesWithMeta.forEach((n) => {
    if (n.isFinal || !n.dropped) return;
    const dropName = `${n.name} · Drop-off`;
    dropNodes.push({
      name: dropName,
      isDrop: true,
      dropFrom: n.name,
      users: n.dropped,
      dropRate: n.dropRate,
      value: minNodeValue,
      depth: (n.depth ?? 0) + 1,
      itemStyle: { color: DROP_COLOR }
    });
    dropLinks.push({
      source: n.name,
      target: dropName,
      value: Math.min(n.dropped, maxFlow),
      realValue: n.dropped,
      isDrop: true,
      showPill: true,
      lineStyle: { color: DROP_COLOR, opacity: 0.35 }
    });
  });

  // Edge pills: one per flow (on its first segment), grey for drop-offs.
  const sankeyLinks = [...flowLinks, ...dropLinks].map((l, i) => {
    const key = `pill_${i}`;
    rich[key] = {
      backgroundColor: l.isDrop ? "#6b7280" : l.lineStyle.color,
      color: "#fff",
      padding: [2, 6],
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 700
    };
    return { ...l, _pillKey: key };
  });

  // Header row mirrors the primary funnel stages, in whatever order and
  // however many the backend returns.
  const stepLabels = (funnelData?.funnel || [])
    .map((f) => f?.stage)
    .filter((stage, i, arr) => stage && arr.indexOf(stage) === i);

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      confine: true,
      position: (point) => [point[0] + 16, point[1] - 40],
      formatter: (params) => {
        const d = params.data;
        if (params.dataType === "edge") {
          if (d.isDrop) return `${d.source} → Drop-off<br/><b>${d.realValue}</b> users left`;
          return `${d.flow.from} → ${d.flow.to}<br/><b>${d.flow.value}</b> users`;
        }
        if (d.isVia) return "";
        if (d.isDrop) {
          return `<b>Drop-off after ${d.dropFrom}</b><br/>${d.users} users (${d.dropRate}%)`;
        }
        return (
          `<b>${params.name}</b><br/>${d.users} users (${d.percent}%)<br/>` +
          (d.isFinal ? `${d.users} converted` : `${d.dropped} dropped (${d.dropRate}%)`)
        );
      }
    },
    series: [
      {
        type: "sankey",
        left: "2%",
        right: "12%",
        top: 20,
        bottom: 20,
        nodeWidth: 16,
        nodeGap: 28,
        nodeAlign: "left",
        // Keep the layered order described above (step / pass-through /
        // drop-off) — no automatic reordering.
        layoutIterations: 0,
        draggable: false,
        emphasis: { focus: "adjacency" },
        // Hide edge pills that would overlap other labels (hover still
        // shows every value). Node labels are never hidden.
        labelLayout: (params) => ({ hideOverlap: params.dataType === "edge" }),
        label: {
          show: true,
          fontSize: 12,
          // Light panel behind each label so ribbons passing underneath
          // don't make it unreadable.
          backgroundColor: "rgba(255,255,255,0.95)",
          borderRadius: 4,
          padding: [3, 6],
          formatter: (params) => {
            const d = params.data;
            if (d.isVia) return "";
            if (d.isDrop) {
              return `{dropTitle|↓ Drop-off}\n{dropCount|${d.users} users (${d.dropRate}%)}`;
            }
            const key = sanitizeKey(params.name);
            return (
              `{title_${key}|${wrapTitle(params.name)}}\n` +
              `{pct_${key}|${d.percent}%}  {count_${key}|${d.users} users}` +
              (d.isFinal ? `\n{done_${key}|✓ ${d.users} converted}` : "")
            );
          },
          rich
        },
        edgeLabel: {
          show: true,
          formatter: (params) =>
            params.data.showPill
              ? `{${params.data._pillKey}|${params.data.realValue ?? params.data.value}}`
              : "",
          rich
        },
        lineStyle: {
          opacity: 0.4,
          curveness: 0.5
        },
        // Order inside each column = order here: steps, pass-through, drop-off.
        data: [...nodesWithMeta, ...viaNodes, ...dropNodes],
        links: sankeyLinks
      }
    ]
  };

  const cards = [
    { title: "Visitors", key: "visitors", icon: <Users size={18} />, tone: "primary" },
    { title: "Sessions", key: "sessions", icon: <BarChart3 size={18} />, tone: "info" },
    { title: "Carts", key: "addToCart", icon: <ShoppingCart size={18} />, tone: "success" },
    { title: "Payments", key: "payments", icon: <CreditCard size={18} />, tone: "warning" },
  ];
  const maxUsers = metricsData?.current?.visitors || 1;

  return (
    <CustomPageLayout title="Funnel Analysis">
      <div className="row g-3">
        {/* ---------- left: date, traffic sources, drop-offs ---------- */}
        <div className="col-xl-3 col-lg-4 col-12 d-flex flex-column gap-3">
          <section className="bg-white border rounded-3 shadow-sm p-3">
            <CustomDatePicker label="Date" isRequired value={date} maxDate={moment().format("YYYY-MM-DD")} onChange={setDate} clearable={false} noMargin />
          </section>

          <Panel title="Traffic Sources" extra={<span className="small text-muted">{sourcesData.length}</span>}>
            <div className="p-2 overflow-auto" style={{ maxHeight: 280 }}>
              {sourcesData.length === 0 && <div className="small text-muted text-center py-3">{loading ? "Loading…" : "No sources for this day."}</div>}
              {sourcesData.map((item, index) => {
                const active = item?.page === selectedSource?.page;
                return (
                  <button
                    type="button"
                    key={index}
                    className={`btn w-100 text-start rounded-2 px-2 py-2 mb-1 border-0 ${active ? "bg-primary bg-opacity-10" : "bg-transparent"}`}
                    onClick={() => setSelectedSource(item)}
                    aria-pressed={active}
                  >
                    <div className="d-flex justify-content-between small fw-semibold">
                      <span className={`text-truncate ${active ? "text-primary" : ""}`}>{item?.page}</span>
                      <span>{(item?.users ?? 0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="progress mt-1" style={{ height: 5 }} aria-hidden="true">
                      <div className="progress-bar" style={{ width: `${((item?.users ?? 0) / maxUsers) * 100}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>

          <Panel title="Drop Off Analysis">
            <div className="p-2 overflow-auto" style={{ maxHeight: 280 }}>
              {!(funnelData?.dropOff?.length > 0) && <div className="small text-muted text-center py-3">No drop-offs to show.</div>}
              {funnelData?.dropOff?.map((item, index) => (
                <div className="d-flex justify-content-between align-items-center gap-2 small border-bottom py-2" key={index}>
                  <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                    {item?.from} <ArrowRight size={13} className="text-muted" /> {item?.to}
                  </span>
                  <span className="fw-bold text-danger">{item?.dropOffRate ? `${item.dropOffRate.toFixed(2)}%` : "0%"}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* ---------- right: metrics + funnel ---------- */}
        <div className="col-xl-9 col-lg-8 col-12 d-flex flex-column gap-3">
          <div className="row g-3">
            {cards.map((c) => {
              const vs = vsYesterday(metricsData?.current?.[c.key], metricsData?.previous?.[c.key]);
              return (
                <CustomStatCard
                  key={c.key}
                  colClass="col-xl-3 col-sm-6 col-12"
                  title={c.title}
                  value={(metricsData?.current?.[c.key] || 0).toLocaleString("en-IN")}
                  subtitle={vs.subtitle}
                  trend={vs.trend}
                  icon={c.icon}
                  tone={c.tone}
                  loading={loading}
                />
              );
            })}
          </div>

          <Panel
            title={`Funnel Activity${selectedSource?.page ? ` · ${selectedSource.page}` : ""}`}
            extra={<span className="small text-muted">{moment(date).format("DD/MM/YYYY")}</span>}
          >
            <div className="p-2">
              {funnelData?.nodes && funnelData?.nodes?.length > 0 ? (
                <div className="overflow-auto">
                  <div style={{ minWidth: 760 }}>
                    <div className="d-flex" style={{ padding: "0 10% 8px 2%" }}>
                      {stepLabels.map((name, i) => (
                        <div key={name} className="flex-fill" style={i > 0 ? { borderLeft: "1px solid #e1e0d9", paddingLeft: 10 } : {}}>
                          <div className="text-muted fw-bold" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                            Step {i + 1} ›
                          </div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {name}
                          </div>
                        </div>
                      ))}
                    </div>
                    <ReactECharts option={option} style={{ height: 600 }} />
                  </div>
                </div>
              ) : (
                <CustomEmptyState title="No funnel data" description={sourcesData.length ? "Pick a traffic source on the left." : "No traffic for this day."} />
              )}
            </div>
          </Panel>
        </div>
      </div>
    </CustomPageLayout>
  );
};

export default FunnelAnalysis;
